/**
 * Grade Sync Service - Admin Portal
 * 
 * Service để gọi API đồng bộ điểm từ CSDL phụ
 */

const API_BASE = '/api/v1/grade-sync';

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

interface GradeTable {
  tableName: string;
  khoaHocId: string;
}

interface GradeSyncResult {
  success: boolean;
  khoaHocId: string;
  totalRecords: number;
  syncedRecords: number;
  updatedRecords: number;
  insertedRecords: number;
  errors: string[];
  duration: number;
}

interface SyncAllResult {
  totalTables: number;
  totalSynced: number;
  totalInserted: number;
  totalUpdated: number;
  totalDuration: number;
  failedTables: string[];
  details: GradeSyncResult[];
}

interface GradeStatistics {
  overview: {
    totalRecords: number;
    totalStudents: number;
    totalSubjects: number;
    recordsWithGrades: number;
    failedRecords: number;
    avgGrade: number;
    lastSyncAt: string;
  };
  gradeDistribution: Array<{
    DiemChu: string;
    count: number;
  }>;
}

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
});

export const GradeSyncService = {
  /**
   * Lấy danh sách bảng điểm có thể đồng bộ
   */
  async getGradeTables(): Promise<ApiResponse<GradeTable[]>> {
    const response = await fetch(`${API_BASE}/admin/tables`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  /**
   * Đồng bộ điểm từ một bảng
   */
  async syncGradesFromTable(tableName: string): Promise<ApiResponse<GradeSyncResult>> {
    const response = await fetch(`${API_BASE}/admin/sync/${tableName}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return response.json();
  },

  /**
   * Đồng bộ tất cả điểm
   */
  async syncAllGrades(): Promise<ApiResponse<SyncAllResult>> {
    const response = await fetch(`${API_BASE}/admin/sync-all`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return response.json();
  },

  /**
   * Đồng bộ điểm cho một sinh viên
   */
  async syncGradesForStudent(maHS: string): Promise<ApiResponse<GradeSyncResult>> {
    const response = await fetch(`${API_BASE}/admin/sync-student/${maHS}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return response.json();
  },

  /**
   * Lấy thống kê điểm
   */
  async getGradeStatistics(): Promise<ApiResponse<GradeStatistics>> {
    const response = await fetch(`${API_BASE}/admin/statistics`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  /**
   * Lấy điểm của sinh viên (admin)
   */
  async getStudentGrades(maHS: string): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_BASE}/admin/student-grades?maHS=${maHS}`, {
      headers: getAuthHeaders()
    });
    return response.json();
  },

  /**
   * Đồng bộ điểm theo khóa học
   */
  async syncGradesByKhoaHoc(khoaHocId: string): Promise<ApiResponse<GradeSyncResult>> {
    const response = await fetch(`${API_BASE}/admin/sync-khoahoc/${khoaHocId}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return response.json();
  },

  /**
   * Đồng bộ điểm theo môn học
   */
  async syncGradesByMonHoc(khoaHocId: string, monHocId: string): Promise<ApiResponse<GradeSyncResult>> {
    const response = await fetch(`${API_BASE}/admin/sync-monhoc/${khoaHocId}/${monHocId}`, {
      method: 'POST',
      headers: getAuthHeaders()
    });
    return response.json();
  },

  /**
   * Lấy lịch sử đồng bộ gần đây
   */
  async getSyncHistory(limit: number = 20): Promise<ApiResponse<any[]>> {
    const response = await fetch(`${API_BASE}/admin/sync-history?limit=${limit}`, {
      headers: getAuthHeaders()
    });
    return response.json();
  }
};

export default GradeSyncService;

